/**
 * Journey illustrations (spec-38 WP5). Images live in the private
 * `deal-documents` bucket, so a markdown body can never hold a working URL:
 * signed URLs expire after 300s. Instead the body holds a stable
 * `journey-image:{dealId}/{name}` ref that the renderer exchanges for a fresh
 * signed URL on display. That also means the renderer can refuse anything that
 * is not one of our own refs, so no external image is ever loaded.
 */

const PREFIX = 'journey-image:'

const REF = new RegExp(
  `^${PREFIX}([0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12})/` +
    `([0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}\\.(?:png|jpg|webp|gif))$`,
)

export type JourneyImageRef = { dealId: string; name: string }

/** The parsed ref, or `null` for anything that is not one of our own images. */
export function parseJourneyImageRef(src: string): JourneyImageRef | null {
  const match = REF.exec(src.trim())
  if (match === null) return null
  return { dealId: match[1], name: match[2] }
}

/** The markdown line to append to an item's content after an upload. */
export function journeyImageMarkdown(alt: string, ref: string): string {
  // `]` and `)` would end the markdown span early and leave a broken ref.
  return `![${alt.replace(/[[\]()]/g, ' ').trim()}](${ref})`
}

async function authHeaders(): Promise<Record<string, string>> {
  // Imported lazily: `MarkdownLite` is a generic renderer used all over the
  // app, and `@/lib/supabase` throws at import time without its env vars.
  const { supabase } = await import('@/lib/supabase')
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`
  return headers
}

const base = (dealId: string) => `/api/v1/preinvest/deals/${dealId}/journey/images`

/** Exchange a stored image name for a short-lived signed URL. */
export async function fetchJourneyImageUrl(dealId: string, name: string): Promise<string> {
  const response = await fetch(`${base(dealId)}/${name}`, { headers: await authHeaders() })
  if (!response.ok) throw new Error(`Afbeelding laden mislukt (${response.status})`)
  const body = (await response.json()) as { url: string }
  return body.url
}

/** Upload one illustration and return the ref to embed in the markdown. */
export async function uploadJourneyImage(
  dealId: string,
  file: File,
): Promise<{ name: string; ref: string }> {
  const form = new FormData()
  form.append('file', file)
  const response = await fetch(base(dealId), {
    method: 'POST',
    // No Content-Type header: the browser sets the multipart boundary.
    headers: await authHeaders(),
    body: form,
  })
  if (!response.ok) throw new Error(`Uploaden mislukt (${response.status})`)
  return (await response.json()) as { name: string; ref: string }
}
